import React from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiAlertCircle, mdiInformation, mdiPencil } from '@lumapps/lumx/icons';
import { Button, Emphasis, Icon, Text } from '@lumapps/lumx/react';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { useSelector } from '@lumapps/redux/react';
import { Link, useOnNavigation } from '@lumapps/router';
import { useTranslate } from '@lumapps/translations';

import {
    CONTENT_PUBLICATION_STATUS,
    CONTENT_PUBLICATION_STATUS_WITH_NOTIFICATION,
    CONTENT_STATUS_NOTIFICATION_STATUS,
    type ContentStatusWithNotification,
} from '../constants';
import {
    getCurrentContentPublicationStatus,
    isInDesignerMode,
    shouldUserBeRedirectedToWriterEditor,
} from '../ducks/selectors';
import { CONTENTS } from '../keys';
import { contentEdit } from '../routes';

const CLASSNAME = 'content-status-notification';

interface UseContentStatusNotificationParams {
    contentId?: string;
    currentContentType?: string;
    /** Whether the notification can be displayed */
    isNotificationDisplayable: boolean;
}

export const useContentStatusNotification = ({
    contentId,
    currentContentType,
    isNotificationDisplayable = false,
}: UseContentStatusNotificationParams) => {
    const { translateKey } = useTranslate();
    const { get } = useDataAttributes(CLASSNAME);
    const { extended, remove } = useNotification();

    const isInDesigner = useSelector(isInDesignerMode);
    const contentPublicationStatus = useSelector(getCurrentContentPublicationStatus);

    const [currentNotificationId, setCurrentNotificationId] = React.useState<undefined | string>();
    const notificationId = React.useMemo(() => `${CLASSNAME}-${contentId}`, [contentId]);
    const notificationStatus = React.useMemo<ContentStatusWithNotification | undefined>(() => {
        // Handle published content with revision requested on last version
        if (
            contentPublicationStatus.publicationStatus === CONTENT_PUBLICATION_STATUS.PUBLISHED &&
            contentPublicationStatus.isRevisionRequested
        ) {
            return CONTENT_STATUS_NOTIFICATION_STATUS.PUBLISHED_WITH_REFUSED_VERSION;
        }

        // Handle published content with waiting validation on last version
        if (
            contentPublicationStatus.publicationStatus === CONTENT_PUBLICATION_STATUS.PUBLISHED &&
            contentPublicationStatus.isAwaitingValidation
        ) {
            return CONTENT_STATUS_NOTIFICATION_STATUS.PUBLISHED_WITH_TO_VALIDATE_VERSION;
        }

        // Handle draft content with revision requested
        if (contentPublicationStatus.isRevisionRequested) {
            return CONTENT_STATUS_NOTIFICATION_STATUS.REFUSED;
        }

        // Handle soon expiring content
        if (contentPublicationStatus.isExpiringSoon) {
            return CONTENT_STATUS_NOTIFICATION_STATUS.EXPIRES_SOON;
        }

        // Handle draft content with waiting validation
        if (contentPublicationStatus.isAwaitingValidation) {
            return CONTENT_STATUS_NOTIFICATION_STATUS.TO_VALIDATE;
        }

        // Handle published content
        if (contentPublicationStatus.publicationStatus === CONTENT_PUBLICATION_STATUS.PUBLISHED) {
            return undefined;
        }

        return contentPublicationStatus.publicationStatus;
    }, [
        contentPublicationStatus.isAwaitingValidation,
        contentPublicationStatus.isExpiringSoon,
        contentPublicationStatus.isRevisionRequested,
        contentPublicationStatus.publicationStatus,
    ]);

    const isPublicationStatusRequireNotification =
        notificationStatus && Object.keys(CONTENT_PUBLICATION_STATUS_WITH_NOTIFICATION).includes(notificationStatus);
    const contentStatusNotificationInfo = isPublicationStatusRequireNotification
        ? CONTENT_PUBLICATION_STATUS_WITH_NOTIFICATION[notificationStatus]
        : undefined;
    const shouldDisplayNotification =
        !!contentId && isNotificationDisplayable && !!contentStatusNotificationInfo && !isInDesigner;

    const removeContentStatusNotification = React.useCallback(() => {
        remove(notificationId);
        setCurrentNotificationId(undefined);
    }, [remove, notificationId]);
    const shouldOpenWriterEditor = useSelector(shouldUserBeRedirectedToWriterEditor(currentContentType));
    const showContentStatusNotification = React.useCallback(() => {
        if (shouldDisplayNotification && !currentNotificationId) {
            setCurrentNotificationId(notificationId);
            extended({
                id: notificationId,
                title: translateKey(contentStatusNotificationInfo.title),
                onNavigationChange: removeContentStatusNotification,
                figure: (
                    <Icon
                        icon={
                            contentStatusNotificationInfo.notificationType === 'warning'
                                ? mdiAlertCircle
                                : mdiInformation
                        }
                        color={contentStatusNotificationInfo.notificationType === 'warning' ? 'yellow' : 'blue'}
                        size="m"
                    />
                ),
                content: (
                    <>
                        <Text as="p" typography="body1" color="dark" style={{ textAlign: 'center' }}>
                            {translateKey(contentStatusNotificationInfo.description)}
                        </Text>
                        <Text as="p" typography="body1" color="dark" style={{ textAlign: 'center' }}>
                            {translateKey(CONTENTS.STATUS_NOTIFICATION_DESCRIPTION)}
                        </Text>
                    </>
                ),

                actions: (
                    <Button
                        leftIcon={mdiPencil}
                        emphasis={Emphasis.medium}
                        linkAs={Link}
                        to={contentEdit(contentId, { shouldOpenWriterEditor })}
                        {...get({ element: 'button', action: `edit-content-${contentPublicationStatus}` })}
                    >
                        {translateKey(CONTENTS.EDIT_THIS_CONTENT)}
                    </Button>
                ),
            });
        }
    }, [
        contentId,
        contentPublicationStatus,
        contentStatusNotificationInfo?.description,
        contentStatusNotificationInfo?.notificationType,
        contentStatusNotificationInfo?.title,
        currentNotificationId,
        extended,
        get,
        notificationId,
        shouldDisplayNotification,
        shouldOpenWriterEditor,
        translateKey,
        removeContentStatusNotification,
    ]);

    // Display the notification on init
    React.useEffect(() => {
        showContentStatusNotification();
    }, [showContentStatusNotification]);

    /** Force the notification to be removed on isNotificationDisplayable going to false
     * Used for legacy route change
     */
    React.useEffect(() => {
        if (!isNotificationDisplayable) {
            removeContentStatusNotification();
        }
    }, [isNotificationDisplayable, removeContentStatusNotification]);

    // Display the notification on content link click (react context -> react context)
    useOnNavigation(() => {
        if (!currentNotificationId) {
            showContentStatusNotification();
        }
    }, [currentNotificationId]);
};
